import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  input,
  signal,
  effect,
  output
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { filter, map, Observable, tap } from 'rxjs';
import { EnvironmentService } from '../../../environments/environment.service';
import { Content, ContentVisualizationType } from '../../domain/content/content.model';
import { genericFile } from '../navigation/navigation.model';
import { TranslateModule } from '@ngx-translate/core';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { VideoListCardComponent } from './cards/video-list-card/video-list-card.component';
import { StandardCardComponent } from './cards/standard-card/standard-card.component';
import { GridCardComponent } from './cards/grid-card/grid-card.component';
import { GenericCardComponent } from './cards/generic-card/generic-card.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';

const SKELETON_ELEMENTS_MAP: {
  cardType: ContentVisualizationType[];
  numberOfElements: number;
}[] = [
    {
      cardType: [ContentVisualizationType.RubricaCard],
      numberOfElements: 1
    },
    {
      cardType: [
        ContentVisualizationType.OnlyTitleCard,
        ContentVisualizationType.VideoCardDxColumn,
        ContentVisualizationType.NoImageCardColumn,
        ContentVisualizationType.ListCardDxColumn
      ],
      numberOfElements: 3
    },
    {
      cardType: [
        ContentVisualizationType.StandardCard,
        ContentVisualizationType.ListVideoCard,
        ContentVisualizationType.ListCard,
        ContentVisualizationType.SearchCard,
        ContentVisualizationType.GridCard,
        ContentVisualizationType.NoImageCardHome,
        ContentVisualizationType.NoImageCard
      ],
      numberOfElements: 6
    }
  ];

@Component({
  selector: 'dottnet-content-visualizer',
  templateUrl: './content-list-viewer.component.html',
  styleUrls: ['./content-list-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'width: 100%'
  },
  imports: [NgClass, NgTemplateOutlet, GenericCardComponent, NgStyle, RouterLink, GridCardComponent, StandardCardComponent, VideoListCardComponent, SkeletonComponent, TranslateModule]
})
export class ContentListViewerComponent implements OnInit {
  // Reference to access the enum in the template
  readonly ContentVisualizationType = ContentVisualizationType;

  // Cambia da Observable<Content[]> a Content[]
  readonly contents = input<Content[]>([]);
  readonly visualizationType = input<ContentVisualizationType>(ContentVisualizationType.StandardCard);

  readonly title = input<string>(null);
  readonly titleCssClass = input<string>(null);

  readonly buttonAction = input<string>(null);
  readonly cardFlexBasis = input<number>(100);

  readonly isHomePage = input<boolean>(false);
  readonly isVideo = input<boolean>(false);

  readonly more = input<boolean>(false);
  readonly howMany = input<number>(0);
  readonly applyDeferring = input.required<boolean>();

  readonly moreEvent = output();

  contentRendered: boolean = false;
  templateFromRoute: string;

  skeletonElementsNumber: number = 0;

  // Internal signal per i contenuti processati
  contentInternal = signal<Content[]>([]);

  constructor(
    private environmentService: EnvironmentService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // Effect per aggiornare il signal interno quando cambia l'input
    effect(() => {
      const contentsValue = this.contents();

      // Controlla se ci sono contenuti e se è un array
      if (contentsValue && Array.isArray(contentsValue)) {
        if (contentsValue.length === 0) {
          this.onContentRendered(false);
        } else {
          // Processa i contenuti e aggiorna il signal interno
          const processedContents = contentsValue.map(content => ({
            ...content,
            pathFoto: this.getPathFoto(content)
          }));

          this.contentInternal.set(processedContents);
          this.onContentRendered(true);
        }
      } else {
        // Se non è un array o è null/undefined, inizializza con array vuoto
        this.contentInternal.set([]);
        this.onContentRendered(false);
        console.warn('contents è null, undefined o non è un array:', contentsValue);
      }
    });
  }

  ngOnInit(): void {
    this.templateFromRoute = this.router.url.split('/')[1];

    this.skeletonElementsNumber = SKELETON_ELEMENTS_MAP.find((elem) =>
      elem.cardType.includes(this.visualizationType())
    ).numberOfElements;
  }

  getPathFoto(content: Content) {
    let pathFoto = genericFile;
    if (content.pathFoto) {
      if (content.pathFoto.startsWith('http')) {
        pathFoto = content.pathFoto;
      } else {
        pathFoto = `${this.environmentService.contentPath}${content.pathFoto}`;
      }
    }
    return pathFoto;
  }

  trackByFn(index, item: Content) {
    return item.idContenuto;
  }

  onContentRendered(val: boolean) {
    this.contentRendered = val;
    this.changeDetectorRef.detectChanges();
  }

  moreContents() {
    // emetto evento di more
    this.moreEvent.emit();
  }
}
